import React from 'react';
// import Accordion from '@mui/material/Accordion';
// import AccordionSummary from '@mui/material/AccordionSummary';
// import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Logo from 'app/fuse-layouts/shared-components/Logo';
import { type Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import * as Actions from 'app/store/actions';
import { getCookieNagbarAcknowledged, getCookieSettings, getProfile } from 'app/store/reducers';
import { useDispatch, useSelector } from 'app/modules/react-redux';
import { useTranslation } from 'react-i18next';
import { AppBar, Collapse } from '@mui/material';
import { CloseIcon } from 'app/components/Icons';
import clsx from 'clsx';
import useGetPageSection from 'app/hooks/useGetPageSection';

//
// Styles
//

const useStyles = makeStyles((theme: Theme) => ({
	closeButton: {
		position: 'absolute',
		right: theme.spacing(1),
		top: theme.spacing(0.6),
		'[data-tier="starter"] &': {
			color: '#FFFFFF',
		},
	},
	button: {
		marginRight: '14px',
		textTransform: 'capitalize',
		width: '276px',
	},
	dialogContent: {
		// paddingTop: '1.5em',
		overflow: 'hidden',
		boxShadow:
			'0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
		margin: 16,
		padding: '16px 0',
		borderRadius: 4,
		'& > .MuiPaper-root': {
			padding: '0 16px',
			minHeight: 64,
		},
	},
	logoBackground: {
		'[data-tier="starter"] &': {
			backgroundColor: theme.palette.secondary.main,
		},
	},
	collapseBtn: {
		height: 48,
		borderRadius: 8,
		alignSelf: 'center',
		marginTop: 24,
	},
}));

//
// Components
//
const CookieSettingsDescription = () => {
	const { t } = useTranslation();

	return (
		<DialogContent>
			<Grid item xs container direction="column" spacing={2}>
				<Grid item xs>
					<Typography gutterBottom variant="h6">
						{t('Cookie Settings')}
					</Typography>
					<Typography gutterBottom color="textSecondary">
						{t('cookie consent:description 1')}
					</Typography>
					<Typography gutterBottom color="textSecondary">
						{t('cookie consent:description 2')}
					</Typography>
					<Typography gutterBottom color="textSecondary">
						{t('cookie consent:description 3')}
					</Typography>
				</Grid>
			</Grid>
		</DialogContent>
	);
};

const CookieSettingsList = () => {
	const { t } = useTranslation();
	const classes = useStyles();
	const [showDetails, setShowDetails] = React.useState(true);

	const toggleDetails = () => {
		setShowDetails(!showDetails);
	};

	return (
		<DialogContent className={classes.dialogContent}>
			<Paper elevation={0} style={{ margin: '12px 0', marginTop: 0 }}>
				<Grid container spacing={3}>
					<Grid item xs={10} sm container>
						<Grid item xs container direction="column" spacing={2}>
							<Grid item xs>
								<Typography gutterBottom variant="subtitle1">
									<strong>{t('cookie consent:technically necessary subtitle')}</strong>
								</Typography>
								<Typography variant="body2" color="textSecondary">
									{t('cookie consent:technically necessary text')}
								</Typography>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={2} alignContent="center" container spacing={2}>
						<Grid item>
							<FormControlLabel
								disabled
								className="mt-4 mb-8 mr-8"
								label=""
								onClick={(event) => event.stopPropagation()}
								control={
									<Switch
										checked
										id="cookieSettingsSwitch"
										name="cookieSettingsSwitch"
										onChange={undefined}
									/>
								}
							/>
						</Grid>
					</Grid>
					<IconButton className={classes.collapseBtn} size="large">
						<ExpandMoreIcon
							className={clsx('transition-transform', {
								'rotate-x-180': showDetails,
							})}
							onClick={toggleDetails}
						/>
					</IconButton>
				</Grid>
			</Paper>
			<Divider style={{ display: showDetails ? 'block' : 'none' }} />
			<Collapse style={{ padding: '0 16px', marginTop: 12 }} in={showDetails}>
				<Grid item xs container direction="column" spacing={2}>
					<Grid item xs>
						<Typography gutterBottom variant="subtitle1">
							<strong>{t('cookie consent:session subtitle')}</strong>
						</Typography>
						<Typography variant="body2" color="textSecondary" gutterBottom>
							{t('cookie consent:session text 1')}
						</Typography>
						<Typography variant="body2" color="textSecondary" gutterBottom>
							{t('cookie consent:session text 2')}
						</Typography>
					</Grid>
				</Grid>
				<Grid item xs container direction="column" spacing={2}>
					<Grid item xs>
						<Typography gutterBottom variant="subtitle1">
							<strong>{t('cookie consent:license group selector subtitle')}</strong>
						</Typography>
						<Typography variant="body2" color="textSecondary" gutterBottom>
							{t('cookie consent:license group selector text 1')}
						</Typography>
					</Grid>
				</Grid>
			</Collapse>
		</DialogContent>
	);
};

const Footer = () => {
	const classes = useStyles();
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const handleClose = () => {
		dispatch(Actions.acknowledgeCookieNagbar());
	};

	const handleOnClick = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		dispatch(Actions.acknowledgeCookieNagbar());
		handleClose();
	};

	return (
		<DialogActions>
			<Button
				className={classes.button}
				onClick={handleOnClick}
				color="secondary"
				variant="contained"
				type="submit"
			>
				{t('Accept')}
			</Button>
		</DialogActions>
	);
};

const Header = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const showCookieSettings = useSelector(getCookieSettings);

	const handleClose = () => {
		dispatch(Actions.acknowledgeCookieNagbar());
		dispatch(Actions.setCookieSettings(false));
	};

	return (
		<AppBar position="static" elevation={0}>
			<DialogTitle id="alert-dialog-title" className={classes.logoBackground}>
				<Box color="secondary" style={{ marginLeft: -13 }}>
					<div className="flex flex-1 mx-8">
						<Logo classes={{ logo: 'starter:text-white' }} />
					</div>
				</Box>
				{showCookieSettings ? (
					<IconButton className={classes.closeButton} onClick={handleClose} color="inherit" size="large">
						<CloseIcon />
					</IconButton>
				) : null}
			</DialogTitle>
		</AppBar>
	);
};

//
// Main Component
//

const CookieSettingsDialog = () => {
	const cookieAcknowledged = useSelector(getCookieNagbarAcknowledged);
	const showCookieSettings = useSelector(getCookieSettings);
	const showDialog = !cookieAcknowledged || showCookieSettings;
	const profile = useSelector(getProfile) as ReturnType<typeof getProfile> | undefined; // HACK::use actual type here - assume never undefined within the wrapped app
	const pageSection = useGetPageSection();

	// const handleClose = () => {
	// 	dispatch(Actions.acknowledgeCookieNagbar());
	// };

	return (
		<Dialog
			open={
				showDialog && !pageSection.customLogin && (pageSection.admin || pageSection.manager ? !!profile : true)
			}
			// onClose={handleClose}
			maxWidth="sm"
			aria-labelledby="Accept"
			aria-describedby="Accept"
		>
			<Header />
			<Divider />
			<CookieSettingsDescription />
			<Divider />
			<CookieSettingsList />
			{cookieAcknowledged ? null : <Footer />}
		</Dialog>
	);
};

export default CookieSettingsDialog;
